import { useRef, useState } from 'react'
import {
  Combobox as ComboboxRoot,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@/components/ui/combobox'

type Option = { value: string; label: string }

const CREATE_PREFIX = '__create__'

export function MultiCombobox(props: {
  id?: string
  value: string[]
  onChange: (value: string[], selectedValue: string | undefined) => void
  options: Option[]
  placeholder?: string
  searchPlaceholder: string
  commandEmpty: string
  creatable?: boolean
  createLabel?: (value: string) => string
  onCreateNew?: (value: string) => void
  variant?: 'default' | 'outline'
  children?: React.ReactNode
  className?: string
  isError?: boolean
}) {
  const [searchValue, setSearchValue] = useState('')
  const chipsRef = useRef<HTMLDivElement>(null)

  const optionIds = new Set(props.options.map((o) => o.value))
  const baseOptions = props.creatable
    ? [...props.options, ...props.value.filter((v) => v && !optionIds.has(v)).map((v) => ({ value: v, label: v }))]
    : props.options

  const hasExactMatch = baseOptions.some(
    (o) => o.value.toLowerCase() === searchValue.toLowerCase() || o.label.toLowerCase() === searchValue.toLowerCase(),
  )
  const showCreateOption = props.creatable && searchValue.trim() && !hasExactMatch

  const allItems = !showCreateOption
    ? baseOptions
    : [
        ...baseOptions,
        {
          value: `${CREATE_PREFIX}${searchValue}`,
          label: props.createLabel ? props.createLabel(searchValue) : `Create "${searchValue}"`,
        },
      ]

  const selectedItems = baseOptions.filter((o) => props.value.includes(o.value))

  const labelByValue = new Map(baseOptions.map((o) => [o.value, o.label]))

  return (
    <ComboboxRoot
      modal
      multiple
      items={allItems}
      value={selectedItems}
      onValueChange={(newItems: Option[]) => {
        const createItem = newItems.find((item) => item.value.startsWith(CREATE_PREFIX))
        if (createItem && props.onCreateNew) {
          const newValue = createItem.value.slice(CREATE_PREFIX.length)
          props.onCreateNew(newValue)
          if (props.creatable) setSearchValue('')
          return
        }

        const newValues = newItems.map((item) => {
          if (item.value.startsWith(CREATE_PREFIX)) {
            return item.value.slice(CREATE_PREFIX.length)
          }
          return item.value
        })
        const addedValue = newValues.find((v) => !props.value.includes(v))
        props.onChange(newValues, addedValue)
        if (addedValue && props.creatable) setSearchValue('')
      }}
      onInputValueChange={(inputValue) => setSearchValue(inputValue)}
      isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
      filter={
        props.creatable
          ? (item: Option, query: string) => {
              if (item.value.startsWith(CREATE_PREFIX)) return true
              if (!query) return true
              return item.label.toLowerCase().includes(query.toLowerCase())
            }
          : undefined
      }
    >
      <ComboboxChips ref={chipsRef} className={props.className}>
        <ComboboxValue>
          {props.value.filter(Boolean).map((v) => (
            <ComboboxChip key={v}>{labelByValue.get(v) ?? v}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          id={props.id}
          aria-invalid={props.isError || undefined}
          placeholder={props.searchPlaceholder}
        />
      </ComboboxChips>
      <ComboboxContent anchor={chipsRef}>
        <ComboboxEmpty>{props.commandEmpty}</ComboboxEmpty>
        <ComboboxList>
          {(item: Option) => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  )
}
